import type { ContentRepository } from "../../content/repository";
import type {
  EnemyBehavior,
  EntityDefinitionDocument,
  EntityTypeId,
  GeneratorId,
} from "../../content/types";
import type { EnemyConstruction, Entity } from "../Entity";
import { BossEntity } from "./BossEntity";
import { GeneratorEntity } from "./GeneratorEntity";
import { HeavyMeleeEntity } from "./HeavyMeleeEntity";
import { MeleeEntity } from "./MeleeEntity";
import { MineEntity } from "./MineEntity";
import { RangedEntity } from "./RangedEntity";
import { resolveEntitySprite } from "./sprites";

export interface EntitySpawnOptions {
  readonly levelWidth: number;
  readonly levelHeight: number;
  readonly variant?: string;
  readonly target?: GeneratorId;
}

/** Builds authored enemies from repository definitions and compiled asset registries. */
export class EntityFactory {
  constructor(private readonly content: ContentRepository) {}

  definition(entityType: EntityTypeId): EntityDefinitionDocument {
    return this.content.entity(entityType);
  }

  create(
    entityType: EntityTypeId,
    x: number,
    y: number,
    options: EntitySpawnOptions,
  ): Entity {
    const definition = this.definition(entityType);
    const variant =
      options.variant == null
        ? undefined
        : definition.variants?.[options.variant];
    if (options.variant != null && variant === undefined) {
      throw new Error(
        `unknown variant '${options.variant}' for entity '${entityType}'`,
      );
    }
    const sprite = resolveEntitySprite(variant?.sprite ?? definition.sprite);
    const init: EnemyConstruction = {
      entityType,
      behavior: definition.behavior,
      stats: variant?.stats ?? definition.stats,
      behaviorData: variant?.behaviorData ?? definition.behaviorData,
      spriteClass: sprite.sheet,
      largeEffects: sprite.largeEffects ?? false,
      x,
      y,
      levelWidth: options.levelWidth,
      levelHeight: options.levelHeight,
      initialPreferredDistance:
        definition.behavior === "boss" && variant !== undefined
          ? definition.stats.preferredDistance
          : undefined,
      initialFrameCount:
        definition.behavior === "boss" && variant !== undefined
          ? definition.stats.idleFrameCount
          : undefined,
    };
    const entity = createBehavior(definition.behavior, init);
    entity.rollData.targetID = options.target;
    return entity;
  }
}

function createBehavior(
  behavior: EnemyBehavior,
  init: EnemyConstruction,
): Entity {
  switch (behavior) {
    case "ranged":
      return new RangedEntity(init);
    case "melee":
      return new MeleeEntity(init);
    case "heavy-melee":
      return new HeavyMeleeEntity(init);
    case "mine":
      return new MineEntity(init);
    case "generator":
      return new GeneratorEntity(init);
    case "boss":
      return new BossEntity(init);
  }
}
